import DropdownMultiSelect from '@/components/atoms/Dropdown/DropdownMultiSelect';
import DropdownWithSections from '@/components/atoms/Dropdown/DropdownWithSections';
import { CONTENT_DESCRIPTION_MAX_LENGTH, CONTENT_TITLE_MAX_LENGTH } from '@/constants/contentLimits';
import { formatSubdomainLabel, getSubdomainSectionsForDomains, HEALTH_DOMAINS } from '@/constants/healthDomains';
import { DomainSectionLabel } from '@/modules/content/common/contentDisplay';
import { TContentDomain, TContentType } from '@/types/Content.types';
import { Flex, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import BrandedThumbnailPreview from '../components/BrandedThumbnailPreview';
import { FieldError, FieldLabel } from '../components/FormBits';
import { TBrandedFormErrors, TBrandedFormValues, TVideoSource } from '../types';
import VideoSourceField from '../components/VideoSourceField';

type Props = {
  type: TContentType;
  values: TBrandedFormValues;
  errors: TBrandedFormErrors;
  thumbnailFile: File | null;
  defaultThumbnailUrl?: string | null;
  onField: <K extends keyof TBrandedFormValues>(key: K, value: TBrandedFormValues[K]) => void;
  onDomainsChange: (domains: TContentDomain[]) => void;
  onSubdomainsChange: (subdomains: string[]) => void;
  onThumbnailChange: (file: File | null) => void;
};

// Add/remove an item from a selection array (DropdownMultiSelect fires per click).
const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((value) => value !== item) : [...list, item];

const DetailsStep = ({
  type,
  values,
  errors,
  thumbnailFile,
  defaultThumbnailUrl,
  onField,
  onDomainsChange,
  onSubdomainsChange,
  onThumbnailChange,
}: Props) => {
  const subdomainSections = useMemo(
    () => getSubdomainSectionsForDomains(values.domains),
    [values.domains],
  );

  return (
    <Flex width="100%" gap="48px" flexWrap="wrap" justify="center" marginTop="10px">
      {/* Left: thumbnail */}
      <VStack align="stretch" gap="10px">
        <Text fontWeight={500} fontSize="20px" fontFamily={'Lora'} fontStyle={'italic'} color="text.mediumBlue">
          Content Thumbnail
        </Text>
        <BrandedThumbnailPreview
          type={type}
          title={values.title}
          description={values.description}
          domains={values.domains}
          existingUrl={defaultThumbnailUrl}
          file={thumbnailFile}
          onFileChange={onThumbnailChange}
        />
      </VStack>

      {/* Right: primary details */}
      <VStack align="stretch" gap="14px" flex={1} minWidth="320px" maxWidth="420px">
        <Text fontWeight={500} fontSize="20px" fontFamily={'Lora'} fontStyle={'italic'} color="text.mediumBlue">
          Primary Details
        </Text>

        <VStack align="stretch" gap="4px">
          <Input
            backgroundColor="white"
            placeholder="Title"
            maxLength={CONTENT_TITLE_MAX_LENGTH}
            value={values.title}
            onChange={(e) => onField('title', e.target.value)}
          />
          <FieldError error={errors.title} />
        </VStack>

          <VStack align="stretch" gap="4px">
            <Textarea
              backgroundColor="white"
              placeholder="Summarized Description"
              minHeight="110px"
              maxLength={CONTENT_DESCRIPTION_MAX_LENGTH}
              value={values.description}
              onChange={(e) => onField('description', e.target.value)}
            />
            <HStack justify="space-between">
              <Text fontSize="12px" color="text.mediumGray">
                Maximum {CONTENT_DESCRIPTION_MAX_LENGTH} characters
              </Text>
              <Text fontSize="12px" color="text.mediumGray">
                {values.description.length}/{CONTENT_DESCRIPTION_MAX_LENGTH}
              </Text>
            </HStack>
          <FieldError error={errors.description} />
        </VStack>

        <VStack align="stretch" gap="4px">
          <FieldLabel>Primary Domain(s)</FieldLabel>
          <DropdownMultiSelect
            title="Select domain(s)"
            selectedOptions={values.domains}
            options={HEALTH_DOMAINS}
            width="100%"
            popoverWidth="280px"
            onOptionClick={(option) => onDomainsChange(toggle(values.domains, option))}
          />
          <FieldError error={errors.domains} />
        </VStack>

        <VStack align="stretch" gap="4px">
          <FieldLabel>Secondary Domain(s)</FieldLabel>
          <DropdownWithSections
            title="Select subdomain(s)"
            emptyText="Pick a domain first"
            sections={subdomainSections}
            selectedOptions={values.subdomains}
            sectionLabel={(section) => <DomainSectionLabel domain={section as TContentDomain} />}
            optionLabel={(option) => formatSubdomainLabel(option)}
            width="100%"
            popoverWidth="280px"
            onOptionClick={(option) => onSubdomainsChange(toggle(values.subdomains, option))}
          />
          <FieldError error={errors.subdomains} />
        </VStack>

        {/* Video source (Reel only) lives in the details panel per the design. */}
        {type === 'video' && (
          <VideoSourceField
            source={values.videoSource}
            onSourceChange={(source: TVideoSource) => onField('videoSource', source)}
            file={values.videoFile ?? null}
            onFileChange={(file) => onField('videoFile', file)}
            youtubeUrl={values.youtubeUrl}
            onYoutubeUrlChange={(url) => onField('youtubeUrl', url)}
            existingMuxStatus={values.muxStatus}
            error={errors.video}
          />
        )}
      </VStack>
    </Flex>
  );
};

export default DetailsStep;
